<script lang="ts">
	import classNames from 'classnames';
	import ChevronDownIcon from '../../../../../../../assets/icons/ChevronDownIcon/ChevronDownIcon.svelte';
	import { Link } from '../../../../../../../components/Link';
	import { Txt } from '../../../../../../../components/Txt';
	import { toKebabCase } from '../../../../../../../utils/toKebabCase';
	import type { GlobalNavLink } from '../../../../../types';

	interface Props {
		dataCy: string;
		link: GlobalNavLink;
		pathname: string;
		active?: boolean;
		onClose: () => void;
	}

	let { dataCy, link, pathname, active = false, onClose }: Props = $props();

	let expanded = $state(false);

	$effect(() => {
		if (active) expanded = true;
	});

	const handleToggle = () => {
		expanded = !expanded;
	};

	const handleClickLink = () => {
		onClose();
	};

	const isPathActive = (href?: string) =>
		!!href?.startsWith('/') && (pathname === href || pathname.startsWith(`${href}/`));
	let activeItemHref = $derived(
		link.items
			?.filter((item) => isPathActive(item.href))
			.sort((a, b) => (b.href?.length || 0) - (a.href?.length || 0))[0]?.href
	);
</script>

<button
	data-cy={`${dataCy}-${toKebabCase(link.label)}-link`}
	class={classNames(
		'flex items-center justify-between border-b border-gray-700 px-4 py-4 sm:px-6 w-full',
		active && 'border-l-2 border-l-gray-0 pl-[0.875rem] sm:pl-[1.375rem]'
	)}
	onclick={handleToggle}
	aria-expanded={expanded}
>
	<Txt component="span" variant="body2" class="text-gray-0 tracking-[.14em] uppercase">
		{link.label}
	</Txt>
	{#if active}
		<span class="sr-only">Current section</span>
	{/if}
	<ChevronDownIcon
		color="gray-400"
		class={classNames('h-3 w-3 transition-transform duration-100', {
			'rotate-180': expanded
		})}
	/>
</button>

{#if expanded}
	<div class="border-b border-gray-700 bg-gray-800">
		<div class="py-2 px-6">
			{#each link.items as item}
				{@const itemActive = item.href === activeItemHref}
				<div
					class={classNames('flex items-center gap-2 border-l-2 border-transparent', {
						'border-b border-gray-700': item.isLiveDrops,
						'border-l-gray-0': itemActive,
						'mt-2 border-t border-gray-700 pt-2': item.dividerBefore
					})}
				>
					{#if item.isLiveDrops}
						<div class="h-[8px] w-[8px] bg-green-300 rounded-full ml-1"></div>
					{/if}
					<Link
						href={item.href}
						data-cy={`${dataCy}-${toKebabCase(item.label)}-link`}
						class="block flex-grow py-3.5 pl-3"
						onclick={handleClickLink}
						aria-current={itemActive ? 'page' : undefined}
						{...item.newTab ? { rel: 'noopener noreferrer', target: '_blank' } : {}}
					>
						<Txt variant="body2" class="text-gray-0 tracking-[.14em] uppercase">
							{item.label}
						</Txt>
					</Link>
				</div>
			{/each}
		</div>
	</div>
{/if}
